import type { BrandKit, BrandTemplate, TextStyle } from '@schnitt/shared';
import { kastenRadius, resolveBrandColor } from '@schnitt/shared';

/**
 * Text und Bauchbinde als Bild -- für die Leinwand, die selbst malt (7.69d).
 *
 * Canva macht es genauso: WebGL für das, was die Grafikkarte kann, Canvas 2D
 * für das, was gesetzt werden muss. Hier wird eine Textebene oder eine
 * Bauchbinde in eine Zeichenfläche von der Grösse der Leinwand gemalt; die
 * Grafikkarte bekommt sie dann als Textur und schiebt, dreht und skaliert
 * sie wie jede andere Ebene. Damit stimmt die Stapelung wieder: ein Text
 * *unter* einer Videoebene liegt auch unter ihr.
 *
 * Die Fläche ist immer so gross wie die Leinwand, und der Text steht in ihrer
 * Mitte. Das ist keine Verschwendung, sondern die Regel, die die Rechnung
 * einfach hält: `x`/`y`/`scale`/`rotate` des Clips wirken danach auf die
 * ganze Fläche, genau wie `translate(-50%, -50%)` im Stilblatt auf das
 * Element gewirkt hat. Gemalt wird nur, wenn sich etwas geändert hat (siehe
 * `schluessel`), nicht bei jedem Bild.
 *
 * Was hier steht, hält sich an das Stilblatt, Zeile für Zeile: Zeilenhöhe
 * 1,2, Umbruch bei 86 % der Breite, Rand hinter dem Strich, Kasten mit
 * 0,2em/0,45em Innenabstand und 6 Punkten Rundung. Der Export setzt mit
 * `drawtext`; die kleinen Unterschiede zwischen Browser und ffmpeg bleiben,
 * wie sie vorher auch waren.
 */

export interface TextBild {
  text: string;
  style: TextStyle;
  /** Punkte der Zeichenfläche -- Gerätepunkte, nicht CSS-Punkte. */
  breite: number;
  hoehe: number;
  /** Gerätepunkte je CSS-Punkt, für die Werte, die im Stilblatt in px stehen. */
  dichte: number;
}

/** Womit das Bild gemalt wurde -- gleich heisst: nicht noch einmal malen. */
export function textSchluessel(b: TextBild): string {
  return JSON.stringify([b.text, b.style, b.breite, b.hoehe, b.dichte]);
}

function hexMitDeckung(hex: string, deckung: number): string {
  const a = Math.round(Math.min(1, Math.max(0, deckung)) * 255).toString(16).padStart(2, '0');
  return `${hex}${a}`;
}

/** Der Text in Zeilen, wie der Browser ihn umbrechen würde: am Wort, und harte Umbrüche bleiben. */
function umbrechen(ctx: CanvasRenderingContext2D, text: string, maxBreite: number): string[] {
  const zeilen: string[] = [];
  for (const absatz of text.split('\n')) {
    const woerter = absatz.split(/(\s+)/).filter((w) => w.length > 0);
    let zeile = '';
    for (const wort of woerter) {
      const versuch = zeile + wort;
      if (zeile && ctx.measureText(versuch).width > maxBreite && wort.trim()) {
        zeilen.push(zeile.trimEnd());
        zeile = wort.trimStart();
      } else {
        zeile = versuch;
      }
    }
    zeilen.push(zeile.trimEnd());
  }
  return zeilen;
}

/**
 * Dieselbe Schrift wie das Element: das Stilblatt setzt am Text keine eigene,
 * er erbt sie von der Seite (Poppins, dann die des Systems). `fontFamily` aus
 * dem Stil steht deshalb hier auch nicht -- das Element benutzt sie ebenso
 * wenig, und der Export nimmt ohnehin seine eigene Datei.
 */
function schrift(style: TextStyle, hoehe: number): string {
  const px = Math.max(1, hoehe * style.size);
  const familie = (typeof document !== 'undefined' && getComputedStyle(document.body).fontFamily) || 'sans-serif';
  return `${style.bold ? 700 : 400} ${px}px ${familie}`;
}

/**
 * Eine Textebene in die Mitte der Fläche.
 *
 * Reihenfolge wie das Stilblatt sie ergibt: erst der Kasten, dann je Zeile
 * der Rand (dahinter) und die Füllung (davor), mit Schatten an der Füllung.
 */
export function maleText(flaeche: HTMLCanvasElement, b: TextBild): void {
  flaeche.width = b.breite;
  flaeche.height = b.hoehe;
  const ctx = flaeche.getContext('2d');
  if (!ctx) return;
  ctx.clearRect(0, 0, b.breite, b.hoehe);
  const text = b.style.uppercase ? b.text.toUpperCase() : b.text;
  if (!text.trim()) return;
  ctx.font = schrift(b.style, b.hoehe);
  ctx.textBaseline = 'alphabetic';
  const px = b.hoehe * b.style.size;
  const zeilenHoch = px * 1.2;
  const innenX = b.style.boxColor ? 0.45 * px : 0;
  const innenY = b.style.boxColor ? 0.2 * px : 0;
  const zeilen = umbrechen(ctx, text, b.breite * 0.86 - 2 * innenX);
  const breiteste = Math.max(...zeilen.map((z) => ctx.measureText(z).width));
  const blockBreite = breiteste + 2 * innenX;
  const blockHoehe = zeilen.length * zeilenHoch + 2 * innenY;
  const links = (b.breite - blockBreite) / 2;
  const oben = (b.hoehe - blockHoehe) / 2;

  if (b.style.boxColor) {
    ctx.fillStyle = hexMitDeckung(b.style.boxColor, b.style.boxOpacity);
    // Der Radius wächst mit der Schrift, wie im Film (`kastenRadius`, 7.190):
    // mit festen sechs Punkten hing er an der Grösse des Schirms.
    const r = kastenRadius(px);
    ctx.beginPath();
    ctx.roundRect(links, oben, blockBreite, blockHoehe, r);
    ctx.fill();
  }

  ctx.textAlign = b.style.align;
  const x = b.style.align === 'left' ? links + innenX : b.style.align === 'right' ? links + blockBreite - innenX : b.breite / 2;
  // Die Grundlinie liegt bei etwa 0,8 der Zeilenhöhe -- so sitzt der Text in
  // seiner Zeile, wie der Browser ihn setzt.
  let y = oben + innenY + zeilenHoch * 0.8;
  for (const zeile of zeilen) {
    if (b.style.outlineColor && b.style.outlineWidth > 0) {
      ctx.lineJoin = 'round';
      // Im Stilblatt ist der Strich so breit wie Grösse mal Breite -- und er
      // liegt zur Hälfte unter den Buchstaben (paint-order: stroke fill),
      // deshalb hier die volle Breite und danach die Füllung darüber.
      ctx.lineWidth = px * b.style.outlineWidth;
      ctx.strokeStyle = b.style.outlineColor;
      ctx.shadowColor = 'transparent';
      ctx.strokeText(zeile, x, y);
    }
    if (b.style.shadow) {
      ctx.shadowColor = '#000000cc';
      ctx.shadowBlur = 6 * b.dichte;
      ctx.shadowOffsetY = 2 * b.dichte;
    } else {
      ctx.shadowColor = 'transparent';
      ctx.shadowBlur = 0;
      ctx.shadowOffsetY = 0;
    }
    ctx.fillStyle = b.style.color;
    ctx.fillText(zeile, x, y);
    y += zeilenHoch;
  }
  ctx.shadowColor = 'transparent';
}

export interface MarkenBild {
  template: BrandTemplate;
  fields: Record<string, string>;
  brandKit: BrandKit | undefined;
  /** Das Logo, geladen -- oder null, dann bleibt seine Stelle leer. */
  logo: HTMLImageElement | null;
  breite: number;
  hoehe: number;
}

export function markenSchluessel(b: MarkenBild): string {
  return JSON.stringify([
    b.template,
    b.fields,
    b.brandKit?.primary,
    b.brandKit?.secondary,
    b.logo?.currentSrc ?? null,
    b.logo?.complete ?? false,
    b.breite,
    b.hoehe,
  ]);
}

/**
 * Eine Bauchbinde, bildfüllend, Teil für Teil in der Reihenfolge der
 * Vorlage -- Rechtecke, Logo, Text --, jedes an seiner Stelle in Anteilen
 * der Fläche. Dieselben Zahlen wie `BrandLayer` im Element.
 */
export function maleMarke(flaeche: HTMLCanvasElement, b: MarkenBild): void {
  flaeche.width = b.breite;
  flaeche.height = b.hoehe;
  const ctx = flaeche.getContext('2d');
  if (!ctx) return;
  ctx.clearRect(0, 0, b.breite, b.hoehe);
  const kit = b.brandKit ?? { primary: '#e11d48', secondary: '#0f172a' };
  for (const layer of b.template.layers) {
    if (layer.type === 'rect') {
      ctx.globalAlpha = layer.opacity;
      ctx.fillStyle = resolveBrandColor(kit, layer.color);
      ctx.fillRect(layer.x * b.breite, layer.y * b.hoehe, layer.w * b.breite, layer.h * b.hoehe);
      ctx.globalAlpha = 1;
    } else if (layer.type === 'logo') {
      if (!b.logo?.complete || !b.logo.naturalWidth) continue;
      const w = layer.w * b.breite;
      const h = (w * b.logo.naturalHeight) / b.logo.naturalWidth;
      ctx.globalAlpha = layer.opacity;
      ctx.drawImage(b.logo, layer.x * b.breite, layer.y * b.hoehe, w, h);
      ctx.globalAlpha = 1;
    } else if (layer.type === 'text') {
      const wert = b.fields[layer.slot] ?? layer.placeholder;
      if (!wert.trim()) continue;
      const text = layer.style.uppercase ? wert.toUpperCase() : wert;
      ctx.font = schrift(layer.style, b.hoehe);
      ctx.textBaseline = 'alphabetic';
      ctx.textAlign = layer.style.align;
      ctx.fillStyle = layer.color ? resolveBrandColor(kit, layer.color) : layer.style.color;
      ctx.shadowColor = 'transparent';
      const px = b.hoehe * layer.style.size;
      // Im Element steht der Text mit seiner Oberkante auf y; die Grundlinie
      // liegt eine knappe Schrifthöhe darunter.
      let y = layer.y * b.hoehe + px * 0.96;
      for (const zeile of text.split('\n')) {
        ctx.fillText(zeile, layer.x * b.breite, y, layer.maxWidth * b.breite);
        y += px * 1.2;
      }
    }
  }
}
